import { formatDate } from "@angular/common";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";

import { ServiceHelper } from "@core/helpers/service-helper";
import {
  ExtendedGfhAccount,
  TExtendedGfhAccountsPaginated,
} from "@core/models/extended-gfh-account.model";
import { PendingIban } from "@core/models/pending-iban.model";
import { PendingNav, PendingNavModel } from "@core/models/pending-nav.model";
import { ProductVersions } from "@core/models/product-versions.model";
import { AuthService } from "@core/services/auth.service";
import { TableSorting, TPagination } from "@custom-types/sorting";
import { ApprovalType } from "@enums/approval-type";
import { environment } from "@env/environment";
import { SortingParams } from "@features/+shared/sorting-params/sorting-params";
import { map, Observable } from "rxjs";

@Injectable({
  providedIn: "root",
})
export class DashboardService {
  static readonly ACCESS_TOKEN_KEY = "X-Xsrf-Token";

  get authToken(): string | null {
    return localStorage.getItem(AuthService.ACCESS_TOKEN_KEY);
  }

  constructor(private http: HttpClient) {}

  getPendingNav$(): Observable<Array<PendingNav>> {
    const token = localStorage.getItem(AuthService.ACCESS_TOKEN_KEY);
    const headers = new HttpHeaders({
      "Content-Type": "application/x-www-form-urlencoded",
      "X-Xsrf-Token": `${token}`,
    });
    return this.http
      .get<
        Array<PendingNavModel>
      >(`${environment.BASE_URL}pendingnav`, { headers: headers })
      .pipe(
        map((response: Array<PendingNavModel>): Array<PendingNav> => {
          return response;
        })
      );
  }

  getProductVersionChanged$(
    sorting?: Partial<TPagination>
  ): Observable<Array<ExtendedGfhAccount> | TExtendedGfhAccountsPaginated> {
    const params = ServiceHelper.getListQueryParams(undefined, sorting);
    return this.http
      .get<
        Array<ExtendedGfhAccount> | TExtendedGfhAccountsPaginated
      >(`${environment.BASE_URL}gfhaccount/productversionchanged`, { params })
      .pipe(
        map(
          (
            response: Array<ExtendedGfhAccount> | TExtendedGfhAccountsPaginated
          ): Array<ExtendedGfhAccount> | TExtendedGfhAccountsPaginated => {
            return response;
          }
        )
      );
  }

  getApprovalTaskList$(
    canApprove: boolean,
    sorting?: TableSorting
  ): Observable<Array<PendingIban>> {
    let params = SortingParams.appendParams(sorting);
    params = params.append("canApprove", canApprove);
    return this.http
      .get<
        Array<PendingIban>
      >(`${environment.BASE_URL}dashboard`, { params, withCredentials: true })
      .pipe(
        map((response: Array<PendingIban>): Array<PendingIban> => {
          return response;
        })
      );
  }

  getLastSevenDaysCompletedTaskList$(
    sorting?: TableSorting
  ): Observable<Array<PendingIban>> {
    const today = new Date();
    const sevenDaysAgoDate = new Date(today);
    sevenDaysAgoDate.setDate(today.getDate() - 7);
    const sevenDaysAgo = formatDate(sevenDaysAgoDate, "yyyy-MM-dd", "en-UK");
    const params = SortingParams.appendParams(sorting);
    return this.http
      .get<
        Array<PendingIban>
      >(`${environment.BASE_URL}dashboard?fromDate=${sevenDaysAgo}`, { params, withCredentials: true })
      .pipe(
        map((response: Array<PendingIban>): Array<PendingIban> => {
          return response;
        })
      );
  }

  approveOrDeclineTask$(
    path: number,
    id: string,
    decision: boolean
  ): Observable<unknown> {
    const approveDecline = decision ? "approve" : "decline";
    switch (path) {
      case ApprovalType.PendingCorrection:
        return this.http.patch(
          `${environment.BASE_URL}generalaccountcorrection/${id}/${approveDecline}`,
          id
        );
      case ApprovalType.PendingInternalTransfer:
        return this.http.patch(
          `${environment.BASE_URL}internalTransfer/${id}/${approveDecline}`,
          id
        );
      case ApprovalType.PendingNav:
        return this.http.patch(
          `${environment.BASE_URL}pendingNav/${id}/${approveDecline}`,
          id
        );
      case ApprovalType.PendingMessage:
        return this.http.patch(
          `${environment.BASE_URL}message/${id}/${approveDecline}`,
          id
        );
      case ApprovalType.PendingYieldu:
        return this.http.patch(
          `${environment.BASE_URL}yieldu/${id}/${approveDecline}`,
          id
        );
      case ApprovalType.PendingCompositionVersion:
        return this.http.patch(
          `${environment.BASE_URL}compositionversion/${id}/${approveDecline}`,
          undefined
        );
      case ApprovalType.PendingFund:
        return this.http.patch(
          `${environment.BASE_URL}fund/${id}/${approveDecline}`,
          undefined
        );
      case ApprovalType.PendingDividend:
        return this.http.patch(
          `${environment.BASE_URL}dividend/${id}/${approveDecline}`,
          undefined
        );
      case ApprovalType.PendingGfhAccountCreation:
        return this.http.patch(
          `${environment.BASE_URL}pendinggfhaccountcreation/${id}/${approveDecline}`,
          undefined
        );
      default:
        return this.http.patch(
          `${environment.BASE_URL}pendingIban/${id}/${approveDecline}`,
          id
        );
    }
  }

  getProductVersions$(id: string): Observable<ProductVersions> {
    return this.http
      .get<ProductVersions>(`${environment.BASE_URL}productversion/${id}`)
      .pipe(
        map((response: ProductVersions): ProductVersions => {
          return response;
        })
      );
  }

  saveAccountCostRules$(
    gfhAccountInformation: Partial<ExtendedGfhAccount>
  ): Observable<unknown> {
    const { id, ...information } = gfhAccountInformation;
    return this.http.patch(
      `${environment.BASE_URL}gfhAccount/${id}/costs`,
      information
    );
  }
}
